import React, { useState, useRef } from 'react';
import { Modal, ModalBody, ModalHeader, Progress } from 'reactstrap';
import { fixFailedOrder } from '../../services/api/get-pharmacy-orders';

// Pure helper — also tested in FixFailedOrderModal.test.js
export function parseOrderIds(text) {
  return [...new Set(
    text
      .split(/[,\n]+/)
      .map((s) => s.trim())
      .filter(Boolean)
  )];
}

function makeRow(orderId) {
  return {
    orderId,
    status: 'pending',
    replaced: null,
    cancelled: null,
    refundRequired: null,
    refunded: null,
    newOrderId: null,
    errorMessage: null,
  };
}

function BoolCell({ value }) {
  if (value == null) return <span style={{ color: '#aaa' }}>...</span>;
  return value
    ? <span style={{ color: '#52c41a' }}>✓</span>
    : <span style={{ color: '#ff4d4f' }}>✗</span>;
}

function StatusCell({ status }) {
  if (status === 'pending') return <span style={{ color: '#aaa' }}>...</span>;
  if (status === 'processing') return <span className="spinner-border spinner-border-sm" />;
  if (status === 'skipped') return <span style={{ color: '#aaa' }}>—</span>;
  if (status === 'success') return <span style={{ color: '#52c41a' }}>✓</span>;
  if (status === 'failed') return <span style={{ color: '#ff4d4f' }}>✗</span>;
  return null;
}

function FixFailedOrderModal({ isOpen, onClose }) {
  const [textarea, setTextarea] = useState('');
  const [phase, setPhase] = useState('input'); // 'input' | 'results'
  const [rows, setRows] = useState([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [completedCount, setCompletedCount] = useState(0);
  const stoppedRef = useRef(false);

  const parsedIds = parseOrderIds(textarea);

  const updateRow = (orderId, patch) => {
    setRows((prev) =>
      prev.map((r) => (r.orderId === orderId ? { ...r, ...patch } : r))
    );
  };

  const processId = async (orderId) => {
    updateRow(orderId, { status: 'processing' });
    try {
      const response = await fixFailedOrder(orderId);
      if (response.newOrderDetail?.status === 'success') {
        updateRow(orderId, {
          status: 'success',
          replaced: response.failedOrderDetail?.replaced ?? null,
          cancelled: response.failedOrderDetail?.cancelled ?? null,
          refundRequired: response.failedOrderDetail?.refundRequired ?? null,
          refunded: response.failedOrderDetail?.refunded ?? null,
          newOrderId: response.newOrderDetail?.orderId ?? null,
        });
      } else {
        updateRow(orderId, {
          status: 'failed',
          replaced: response.failedOrderDetail?.replaced ?? null,
          cancelled: response.failedOrderDetail?.cancelled ?? null,
          refundRequired: response.failedOrderDetail?.refundRequired ?? null,
          refunded: response.failedOrderDetail?.refunded ?? null,
          errorMessage: response.message || 'Order fix failed',
        });
      }
    } catch (err) {
      updateRow(orderId, {
        status: 'failed',
        errorMessage: err.response?.data?.message ?? err.message ?? 'Unknown error',
      });
    }
  };

  const handleRunFix = async () => {
    if (isProcessing) return;
    const ids = parsedIds;
    const initialRows = ids.map(makeRow);
    setRows(initialRows);
    setCompletedCount(0);
    setPhase('results');
    setIsProcessing(true);
    stoppedRef.current = false;

    for (const id of ids) {
      if (stoppedRef.current) {
        updateRow(id, { status: 'skipped' });
        setCompletedCount((n) => n + 1);
        continue;
      }
      await processId(id);
      setCompletedCount((n) => n + 1);
    }

    setIsProcessing(false);
  };

  const handleStopAll = () => {
    stoppedRef.current = true;
  };

  const handleRetry = async (orderId) => {
    setIsProcessing(true);
    await processId(orderId);
    setIsProcessing(false);
  };

  const total = rows.length;

  return (
    <Modal isOpen={isOpen} backdrop="static" style={{ maxWidth: '900px', width: '100%' }}>
      <ModalHeader
        style={{ backgroundColor: '#8752CC' }}
        toggle={isProcessing ? undefined : onClose}
      >
        <h4 className="text-center" style={{ color: '#fff', fontWeight: 'bold' }}>
          Fix Failed Orders
        </h4>
      </ModalHeader>

      <ModalBody style={{ padding: '20px 30px' }}>
        {phase === 'input' ? (
          <>
            <div className="form-group">
              <label>Paste order IDs (comma or newline separated)</label>
              <textarea
                className="form-control"
                rows={6}
                value={textarea}
                onChange={(e) => setTextarea(e.target.value)}
                placeholder="e.g. 1001234, 1001235&#10;1001236"
              />
              <small className="text-muted">
                {parsedIds.length} order ID{parsedIds.length !== 1 ? 's' : ''} detected
              </small>
            </div>
            <div className="text-right">
              <button className="btn btn-secondary mr-2" onClick={onClose}>
                Cancel
              </button>
              <button
                className="btn btn-primary"
                disabled={parsedIds.length === 0}
                onClick={handleRunFix}
              >
                Run Fix ({parsedIds.length})
              </button>
            </div>
          </>
        ) : (
          <>
            <div className="d-flex justify-content-between align-items-center mb-2">
              <span>
                {isProcessing
                  ? `Processing: ${completedCount} / ${total}`
                  : `Done: ${completedCount} / ${total}`}
              </span>
              {isProcessing && (
                <button className="btn btn-danger btn-sm" onClick={handleStopAll}>
                  Stop All
                </button>
              )}
            </div>
            <Progress
              value={total > 0 ? (completedCount / total) * 100 : 0}
              className="mb-3"
            />

            <div style={{ overflowX: 'auto' }}>
              <table className="table table-sm table-bordered">
                <thead>
                  <tr>
                    <th>Failed Order ID</th>
                    <th>Replaced</th>
                    <th>Cancelled</th>
                    <th>Refund Req.</th>
                    <th>Refunded</th>
                    <th>New Order ID</th>
                    <th>Status</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => (
                    <tr key={row.orderId}>
                      <td>{row.orderId}</td>
                      <td><BoolCell value={row.replaced} /></td>
                      <td><BoolCell value={row.cancelled} /></td>
                      <td><BoolCell value={row.refundRequired} /></td>
                      <td><BoolCell value={row.refunded} /></td>
                      <td>
                        {row.status === 'pending' || row.status === 'processing'
                          ? '...'
                          : row.status === 'skipped'
                          ? '—'
                          : row.errorMessage
                          ? (
                            <span
                              title={row.errorMessage}
                              style={{ color: '#ff4d4f', cursor: 'help' }}
                            >
                              {row.errorMessage.length > 40
                                ? row.errorMessage.slice(0, 40) + '…'
                                : row.errorMessage}
                            </span>
                          )
                          : row.newOrderId ?? '-'}
                      </td>
                      <td><StatusCell status={row.status} /></td>
                      <td>
                        {row.status === 'failed' && (
                          <button
                            className="btn btn-sm btn-warning"
                            disabled={isProcessing}
                            onClick={() => handleRetry(row.orderId)}
                          >
                            Retry
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {!isProcessing && (
              <div className="text-right mt-2">
                <button className="btn btn-secondary" onClick={onClose}>
                  Close
                </button>
              </div>
            )}
          </>
        )}
      </ModalBody>
    </Modal>
  );
}

export default FixFailedOrderModal;
